import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Select, Spin } from "antd";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import _ from "lodash";
import { getAllLevelsOfAssurance, isLoading } from "../../selectors";
import { getLevelsOfAssurance } from "../../actions";
import { LevelsOfAssurance } from "../../models/levelsOfAssurance";
import PropTypes from "prop-types";

const { Option } = Select;

const EXCLUDED_LEVELS = [
  LevelsOfAssurance.VerifiedExternal,
  LevelsOfAssurance.HEPTrusted,
];

const LoaSelector = (props) => {
  const {
    input,
    actions: { getLevelsOfAssurance },
    loading,
    levelsOfAssurance,
    parentInitialValue,
  } = props;

  const [selectedId, setSelectedId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Filter out excluded levels
  const filteredLevels = useMemo(() => {
    if (!levelsOfAssurance) return [];
    return levelsOfAssurance.filter(
      (item) => !EXCLUDED_LEVELS.includes(item.value),
    );
  }, [levelsOfAssurance]);

  // Find nearest valid level (next higher level if excluded)
  const findNearestValidId = useCallback(
    (targetId) => {
      if (!levelsOfAssurance?.length || !filteredLevels.length) return null;

      const targetLevel = levelsOfAssurance.find((l) => l.id === targetId);
      if (!targetLevel) return null;

      // If already valid, return it
      const isValid = filteredLevels.some((l) => l.id === targetId);
      if (isValid) return targetId;

      // Find next higher valid level
      const higherValidLevels = filteredLevels.filter(
        (l) => l.value >= targetLevel.value,
      );
      return (
        higherValidLevels[0]?.id ||
        filteredLevels[filteredLevels.length - 1]?.id
      );
    },
    [levelsOfAssurance, filteredLevels],
  );

  // Get description with contextual labels
  const getLoaDescription = (item) => {
    if (!item) return "";

    const sorted = _.sortBy(filteredLevels, ["value"]);
    const index = sorted.findIndex((x) => x.id === item.id);

    if (index === 0) return `${item.displayName} (no restrictions)`;
    if (index === 1) return `${item.displayName} (lowest)`;
    if (index === sorted.length - 1) return `${item.displayName} (highest)`;

    return item.displayName;
  };

  const handleChange = (id) => {
    setSelectedId(id);
    input?.onChange?.(id);
  };

  // Load levels on mount
  useEffect(() => {
    getLevelsOfAssurance();
  }, [getLevelsOfAssurance]);

  // Initialize value once when levels load
  useEffect(() => {
    if (!filteredLevels.length || isInitialized) return;

    let initialId = null;

    // Priority 1: Use input.value if provided
    if (input.value) {
      initialId = findNearestValidId(input.value);
    }
    // Priority 2: Use parentInitialValue.minimumLoaId if exists
    else if (parentInitialValue?.minimumLoaId) {
      initialId = findNearestValidId(parentInitialValue.minimumLoaId);
    }
    // Priority 3: Default to CERN (highest security)
    else {
      const cernLevel = filteredLevels.find(
        (l) => l.value === LevelsOfAssurance.CERN,
      );
      initialId = cernLevel?.id;
    }

    if (initialId) {
      setSelectedId(initialId);
      input?.onChange?.(initialId);
      setIsInitialized(true);
    }
  }, [
    filteredLevels,
    input,
    parentInitialValue,
    isInitialized,
    findNearestValidId,
  ]);

  return (
    <>
      {loading ? (
        <Spin />
      ) : (
        <Select
          value={selectedId}
          onChange={handleChange}
          style={{ width: "100%" }}
          popupMatchSelectWidth={false}
          optionLabelProp="label"
        >
          {filteredLevels.map((item) => (
            <Option
              key={item.id}
              value={item.id}
              label={
                <span
                  style={{
                    fontWeight: item.id === selectedId ? "bold" : "normal",
                  }}
                >
                  {getLoaDescription(item)}
                </span>
              }
            >
              {getLoaDescription(item)}
            </Option>
          ))}
        </Select>
      )}
    </>
  );
};

LoaSelector.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.any,
    onChange: PropTypes.func,
  }),
  actions: PropTypes.shape({
    getLevelsOfAssurance: PropTypes.func,
  }),
  loading: PropTypes.bool,
  levelsOfAssurance: PropTypes.array,
  parentInitialValue: PropTypes.any,
};

export default connect(
  (state) => ({
    levelsOfAssurance: getAllLevelsOfAssurance(state),
    loading: isLoading(state, "getLevelsOfAssurance"),
  }),
  (dispatch) => ({
    actions: bindActionCreators(
      {
        getLevelsOfAssurance,
      },
      dispatch,
    ),
  }),
)(LoaSelector);
